import { API_BASE } from "../config";
// =============================================================================
// File: frontend/src/components/BarcodeLookupModal.jsx
// Reusable barcode lookup modal — manual UPC entry + mobile camera scan.
// Extracted from PantryItemForm's working barcode lookup pattern.
// Uses useBarcodeScanner hook for STRICH SDK (single source of truth).
// CSS: Uses standard .modal-overlay / .modal-content (no bleed-through).
//      BLM-specific styles in healist-app-components.css under .blm-* prefix.
// =============================================================================

import React, { useState, useRef, useEffect } from 'react';
import { lookupProductByUPC } from '../utils/barcode-helpers';
import UhcCompatBadge from './UhcCompatBadge';
import EdamamAttribution from './EdamamAttribution';
import { useBarcodeScanner } from '../hooks/useBarcodeScanner';

const BarcodeLookupModal = ({
  onClose,
  onProductFound,   // (barcode, productData) => {}
  fetchWithAuth,
  title = 'Scan Barcode'
}) => {
  const [barcode, setBarcode] = useState('');
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [error, setError] = useState('');
  const [productData, setProductData] = useState(null);
  const [isScanning, setIsScanning] = useState(false);

  const hostElementRef = useRef(null);
  const scannedBarcodesRef = useRef(new Set());

  const { sdkReady, isMobile, startCameraScanning, stopCameraScanning } = useBarcodeScanner({
    fetchWithAuth
  });

  // Cleanup on unmount
  useEffect(() => {
    return () => { stopCameraScanning(); };
  }, [stopCameraScanning]);

  const handleLookup = async () => {
    const trimmed = barcode.trim();
    if (!trimmed) {
      setError('Please enter a barcode');
      return;
    }
    if (!/^\d{8,14}$/.test(trimmed)) {
      setError('Invalid barcode format. Enter 8-14 digits.');
      return;
    }

    setError('');
    setIsLookingUp(true);
    setProductData(null);

    try {
      const result = await lookupProductByUPC(fetchWithAuth, trimmed);
      if (result.found && result.product) {
        const p = result.product;
        setProductData({
          found: true,
          barcode: trimmed,
          name: p.label || 'Unknown Product',
          brand: p.brand,
          category: p.category,
          calories: p.kcal_per_100g,
          protein: p.protein_per_100g,
          carbs: p.carbs_per_100g,
          fat: p.fat_per_100g,
          image: p.image_url,
          serving_size: p.serving_size_g,
          serving_description: p.serving_description,
          healthLabels: p.health_labels || [],
          uhcCompatibility: p.uhc_compatibility,
          productCacheId: result.product_cache_id
        });
      } else {
        setError(result.message || result.error || 'Product not found in database.');
      }
    } catch (err) {
      setError('Lookup failed. Please check your connection.');
    } finally {
      setIsLookingUp(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleLookup();
    }
  };

  const handleUseProduct = () => {
    if (productData && onProductFound) {
      onProductFound(productData.barcode, productData);
    }
  };

  // Camera scanning
  const startScan = async () => {
    setIsScanning(true);
  };

  // Effect to init scanner after DOM renders host element
  useEffect(() => {
    if (!isScanning || !hostElementRef.current) return;

    const timer = setTimeout(async () => {
      const success = await startCameraScanning({
        selector: '#blm-scanner-host',
        onDetection: async (detections) => {
          for (const detection of detections) {
            const code = detection.data;
            if (scannedBarcodesRef.current.has(code)) continue;
            scannedBarcodesRef.current.add(code);

            // Stop scanning, set barcode, auto-lookup
            await stopCameraScanning();
            setIsScanning(false);
            setBarcode(code);

            // Auto-lookup
            setIsLookingUp(true);
            setProductData(null);
            setError('');
            try {
              const result = await lookupProductByUPC(fetchWithAuth, code);
              if (result.found && result.product) {
                const p = result.product;
                setProductData({
                  found: true,
                  barcode: code,
                  name: p.label || 'Unknown Product',
                  brand: p.brand,
                  category: p.category,
                  calories: p.kcal_per_100g,
                  protein: p.protein_per_100g,
                  carbs: p.carbs_per_100g,
                  fat: p.fat_per_100g,
                  image: p.image_url,
                  serving_size: p.serving_size_g,
                  serving_description: p.serving_description,
                  healthLabels: p.health_labels || [],
                  productCacheId: result.product_cache_id
                });
              } else {
                setError(result.message || result.error || 'Product not found.');
              }
            } catch (err) {
              setError('Lookup failed.');
            } finally {
              setIsLookingUp(false);
            }
          }
        }
      });

      if (!success) {
        setIsScanning(false);
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [isScanning]);

  const handleStopScan = async () => {
    await stopCameraScanning();
    setIsScanning(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content blm-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body blm-body">
          {error && <div className="blm-error">{error}</div>}

          {/* Barcode Input + Lookup */}
          <div className="form-group">
            <label className="form-label">Barcode / UPC</label>
            <div className="pf-barcodeInputGroup">
              <input
                type="text"
                className="form-input"
                value={barcode}
                onChange={(e) => setBarcode(e.target.value.replace(/\D/g, ''))}
                onKeyPress={handleKeyPress}
                placeholder="Enter 8-14 digit barcode"
                maxLength={14}
                autoFocus
              />
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleLookup}
                disabled={isLookingUp || !barcode.trim()}
              >
                {isLookingUp ? 'Looking up...' : 'Lookup'}
              </button>
            </div>
          </div>

          {/* Mobile: Scan Button */}
          {isMobile && !isScanning && (
            <div className="form-group">
              <button
                type="button"
                className="btn btn-primary blm-scanBtn"
                onClick={startScan}
                disabled={!sdkReady}
              >
                {sdkReady ? 'Scan Item Barcode' : 'Loading Scanner...'}
              </button>
            </div>
          )}

          {/* Camera Scanner */}
          {isScanning && (
            <div className="blm-scannerSection">
              <div
                id="blm-scanner-host"
                ref={hostElementRef}
                className="blm-scannerHost"
              />
              <button
                type="button"
                className="btn btn-secondary blm-stopBtn"
                onClick={handleStopScan}
              >
                Stop Scanner
              </button>
            </div>
          )}

          {/* Product Result */}
          {productData && (
            <div className="blm-productResult">
              <div className="blm-productInfo">
                {productData.image && (
                  <img src={productData.image} alt="" className="blm-productImage" />
                )}
                <div className="blm-productDetails">
                  <div className="blm-productName">{productData.name}</div>
                  {productData.brand && <div className="blm-productBrand">{productData.brand}</div>}
                  <div className="blm-productNutrition">
                    {productData.calories != null && <span>{Math.round(productData.calories)} cal</span>}
                    {productData.protein != null && <span>{productData.protein}g protein</span>}
                  </div>
                </div>
              </div>
              <UhcCompatBadge compatibility={productData.uhcCompatibility} />
              <EdamamAttribution />
              <button
                type="button"
                className="btn btn-primary blm-useBtn"
                onClick={handleUseProduct}
              >
                Use This Product
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BarcodeLookupModal;
